import { useQuery } from '@tanstack/react-query'
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { fetchAgreementVersion } from '../../api/agreements'
import { agreementLocaleShortLabel } from '../../i18n/agreement-locales'
import { useT } from '../../i18n'
import { termsFullTextI18n } from '../../i18n/onboarding/terms-full-text'

type TermsFullTextModalProps = {
  agreementId: string
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  // Phase 5A: the language the signer is reading + a setter so switching here
  // also updates which locale the acceptance binds to.
  locale: string
  onLocaleChange: (locale: string) => void
}

// Dismissible reader popup for the full agreement text (opens on top of the
// non-dismissible gate). Loads the current effective version in the chosen
// language on demand and offers a language switcher for every available locale.
export function TermsFullTextModal({
  agreementId,
  title,
  open,
  onOpenChange,
  locale,
  onLocaleChange,
}: TermsFullTextModalProps) {
  const t = useT(termsFullTextI18n)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agreement-version', agreementId, locale],
    queryFn: () => fetchAgreementVersion(agreementId, locale),
    enabled: open,
  })

  const available = data?.available_locales ?? [locale]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {data ? (
            <DialogDescription>
              {t.versionLine(data.version, data.status)}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        {available.length > 1 && (
          <div className="flex gap-1" role="group" aria-label={t.languageGroupLabel}>
            {available.map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => onLocaleChange(loc)}
                className={`rounded px-3 py-1 text-xs font-medium ${
                  loc === locale
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`terms-locale-${loc}`}
              >
                {agreementLocaleShortLabel(loc)}
              </button>
            ))}
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-slate-500">{t.loading}</p>
        ) : isError ? (
          <p className="text-sm text-rose-600">
            {t.loadError}
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-4">
            <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-slate-700">
              {data?.content}
            </pre>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
